import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, createBlock as _createBlock, resolveDynamicComponent as _resolveDynamicComponent, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass } from "vue"
import _imports_0 from '@/assets.app/images/onboarding/stepper-gradient.svg'


const _hoisted_1 = {
  key: 0,
  class: "onboarding-questionnaire__header-gradient",
  "aria-hidden": "true"
}
const _hoisted_2 = {
  key: "stepper",
  class: "onboarding-questionnaire__header"
}
const _hoisted_3 = {
  key: 0,
  class: "onboarding-questionnaire__button-wrapper"
}

import { computed, onBeforeMount, onMounted, ref } from 'vue'
import type { Raw } from 'vue'

import { QuestionnaireView } from '@/models/onboarding'
import { storeSampleGuide } from '@/models/onboarding.sample-guide'
import { onboardingPages } from '@/models/onboarding.pages'
import { backend } from '@/services/backend'
import { sampleFirstOnboardingV10_20710Variant } from '@/services/ab/experiments'
import { Sentry } from '@/services/sentry'
import type { AuthUser } from '@/models/interfaces.auth'
import { StorageKeys } from '@/helpers/storage.keys'

import Logo from '@/components/ui/Logo.vue'
import AppButton from '@/app/components/button/AppButton.vue'
import Stepper from '@/app/components/onboarding/Stepper.vue'
import Text from '@/app/components/onboarding/questionnaire/Text.vue'
import DynamicOptions from '@/app/components/onboarding/questionnaire/DynamicOptions.vue'
import Options from '@/app/components/onboarding/questionnaire/Options.vue'
import FinishingSetup from '@/app/components/onboarding/questionnaire/FinishingSetup.vue'
import Review from '@/app/components/onboarding/questionnaire/Review.vue'
import Statements from '@/app/components/onboarding/questionnaire/Statements.vue'
import Books from '@/app/components/onboarding/questionnaire/Books.vue'
import PersonalizedBooks from '@/app/components/onboarding/questionnaire/PersonalizedBooks.vue'
import DailyGoal from '@/app/components/onboarding/questionnaire/DailyGoal.vue'
import Notifications from '@/app/components/onboarding/questionnaire/Notifications.vue'
import CommitmentPact from '@/app/components/onboarding/questionnaire/CommitmentPact.vue'
import Welcome from '@/app/components/onboarding/questionnaire/Welcome.vue'
import Splitscreen from '@/app/components/onboarding/questionnaire/Splitscreen.vue'
import Upgrade from '@/app/components/onboarding/questionnaire/Upgrade.vue'
import GenreSelection from '@/app/components/onboarding/questionnaire/GenreSelection.vue'
import BookSelection from '@/app/components/onboarding/questionnaire/BookSelection.vue'
import Loading from '@/app/components/onboarding/questionnaire/Loading.vue'
import Summary from '@/app/components/onboarding/questionnaire/Summary.vue'
import MillionUsers from '@/app/components/onboarding/questionnaire/MillionUsers.vue'
import ProductDemo from '@/app/components/onboarding/questionnaire/ProductDemo.vue'
import LearningSetupSummary from '@/app/components/onboarding/questionnaire/LearningSetupSummary.vue'
import PlanSelection20676 from '@/app/components/onboarding/questionnaire/PlanSelection20676.vue'
import PaymentCapture20676 from '@/app/components/onboarding/questionnaire/PaymentCapture20676.vue'
import Welcome20710 from '@/app/components/onboarding/questionnaire/Welcome20710.vue'
import BookValidation20710 from '@/app/components/onboarding/questionnaire/BookValidation20710.vue'
import BookSelection20710 from '@/app/components/onboarding/questionnaire/BookSelection20710.vue'
import OptionsPicker20710 from '@/app/components/onboarding/questionnaire/OptionsPicker20710.vue'
import HabitOptions20710 from '@/app/components/onboarding/questionnaire/HabitOptions20710.vue'
import BookPreview20710 from '@/app/components/onboarding/questionnaire/BookPreview20710.vue'
import BookDemo20710 from '@/app/components/onboarding/questionnaire/BookDemo20710.vue'


export default /*@__PURE__*/_defineComponent({
  __name: 'Questionnaire',
  props: {
    nextOnboardingPage: { type: Function }
  },
  setup(__props: any) {

const props = __props

const pageComponents = {
  Text,
  Options,
  DynamicOptions,
  LearningSetupSummary,
  FinishingSetup,
  Review,
  Statements,
  Books,
  PersonalizedBooks,
  DailyGoal,
  Notifications,
  CommitmentPact,
  Welcome,
  Splitscreen,
  Upgrade,
  GenreSelection,
  BookSelection,
  Loading,
  Summary,
  MillionUsers,
  ProductDemo,
  PlanSelection20676,
  PaymentCapture20676,
  Welcome20710,
  BookValidation20710,
  BookSelection20710,
  OptionsPicker20710,
  HabitOptions20710,
  BookPreview20710,
  BookDemo20710,
}

const currentPageComponent = computed(() => {
  return pageComponents[view.value.currentPage.type]
})

const user = JSON.parse(
  localStorage.getItem(StorageKeys.user) || '{}',
) as AuthUser
const firstName = user.full_name?.match(/^[a-zA-ZÀ-ÿ]+/)?.[0]

const view = ref<Raw<QuestionnaireView>>(
  new QuestionnaireView(
    onboardingPages.questionnaire,
    props.nextOnboardingPage,
    firstName,
  ),
)

const isQuestionnaireHeaderVisible = computed(
  () => !view.value.currentPage.hideHeader,
)

const isSummaryPage = computed(
  () => view.value.currentPage.type === 'LearningSetupSummary',
)

const isPersonalizePage = computed(
  () => view.value.currentPage.id === 'personalize',
)

const isCurrentSectionExcludedFromStepper = computed(
  () => view.value.currentSection.excludeFromStepper === true,
)

const isSampleFirstTreatment = computed(
  () => sampleFirstOnboardingV10_20710Variant() === 'B',
)

const showStepperGradient = computed(
  () =>
    !isSampleFirstTreatment.value &&
    isQuestionnaireHeaderVisible.value &&
    (!isCurrentSectionExcludedFromStepper.value || isPersonalizePage.value),
)

onBeforeMount(() => {
  if (sampleFirstOnboardingV10_20710Variant() === 'B') {
    void storeSampleGuide(view.value)
  }
})

onMounted(async () => {
  // Preload cache data while the user goes through the questionnaire.
  // We run it without `await` on purpose so that it doesn't block the
  // loading of the rest of the page.
  preloadCacheData()
})

async function preloadCacheData(): Promise<void> {
  try {
    await Promise.all([
      backend.getBooks(),
      backend.getArticles(),
      backend.getPodcasts(),
      backend.getBookCollections(),
      backend.getRecommendations(),
      backend.getExcerpt(),
      backend.getBookOfTheDay(),
      backend.getUserDocs(),
      backend.getBookTags(),
    ])
  } catch (error: any) {
    Sentry.captureException(error)
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["onboarding-questionnaire onboarding-questionnaire--default", {
      'onboarding-questionnaire--summary': isSummaryPage.value,
      'onboarding-questionnaire--personalize': isPersonalizePage.value,
      'onboarding-questionnaire--20710': isSampleFirstTreatment.value,
    }])
  }, [
    _createVNode(_Transition, { name: "header-gradient" }, {
      default: _withCtx(() => [
        (showStepperGradient.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_1, [...(_cache[1] || (_cache[1] = [
              _createElementVNode("img", {
                class: "onboarding-questionnaire__header-gradient-image",
                src: _imports_0,
                alt: ""
              }, null, -1)
            ]))]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }),
    _createVNode(_Transition, {
      name: "fade",
      mode: "out-in"
    }, {
      default: _withCtx(() => [
        (isQuestionnaireHeaderVisible.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
              (!isCurrentSectionExcludedFromStepper.value)
                ? (_openBlock(), _createBlock(Stepper, {
                    key: 0,
                    view: view.value,
                    "hide-section-name": 
            view.value.currentSection.hideStepperSectionName === true
          
                  }, null, 8, ["view", "hide-section-name"]))
                : (view.value.isLogoVisible)
                  ? (_openBlock(), _createBlock(Logo, {
                      key: 1,
                      class: "onboarding-questionnaire__logo",
                      withText: true
                    }))
                  : _createCommentVNode("", true)
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }),
    _createVNode(_Transition, {
      name: "fade",
      mode: "out-in"
    }, {
      default: _withCtx(() => [
        (_openBlock(), _createBlock(_resolveDynamicComponent(currentPageComponent.value), {
          key: view.value.currentPage.id,
          view: view.value
        }, null, 8, ["view"]))
      ]),
      _: 1
    }),
    _createVNode(_Transition, {
      name: "button-fade",
      mode: "out-in"
    }, {
      default: _withCtx(() => [
        (view.value.isContinueButtonVisible)
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
              _createVNode(AppButton, {
                class: "onboarding-questionnaire__button",
                disabled: view.value.isLoadingNextPage,
                loading: view.value.isLoadingNextPage,
                onClick: _cache[0] || (_cache[0] = ($event: any) => (view.value.continue()))
              }, {
                default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
                  _createTextVNode("Continue", -1)
                ]))]),
                _: 1
              }, 8, ["disabled", "loading"])
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ], 2))
}
}

})